function asFileInfo(value) {
  if (!value) return null;
  if (typeof value === 'string') return { fileUrl: value };
  if (typeof value === 'object') return value;
  return null;
}

/** Use compressed hero variants only when the original is heavier than this. */
export const HEAVY_IMAGE_BYTES = 1024 * 1024; // 1MB

const PRESET_FIELDS = {
  icon: [
    'fileUrlIcon',
    'file_url_icon',
    'fileUrlMinified',
    'file_url_minified'
  ],
  thumb: ['fileUrlMinified', 'file_url_minified'],
  card: [
    'fileUrlSmall',
    'file_url_small',
    'fileUrlMedium',
    'file_url_medium',
    'fileUrlMinified',
    'file_url_minified'
  ],
  hero: [
    'fileUrl',
    'file_url',
    'fileUrlLarge',
    'file_url_large',
    'fileUrlMedium',
    'file_url_medium'
  ],
  heroHeavy: [
    'fileUrlLarge',
    'file_url_large',
    'fileUrlMedium',
    'file_url_medium',
    'fileUrl',
    'file_url'
  ],
  logo: ['fileUrlSmall', 'file_url_small', 'fileUrlMedium', 'file_url_medium'],
  full: ['fileUrl', 'file_url']
};

function getByteSize(info) {
  const size = info.byteSize ?? info.byte_size;
  const n = Number(size);
  return Number.isFinite(n) ? n : null;
}

export function isHeavyFileInfo(fileInfo, thresholdBytes = HEAVY_IMAGE_BYTES) {
  const info = asFileInfo(fileInfo);
  if (!info) return false;
  const byteSize = getByteSize(info);
  return byteSize != null && byteSize > thresholdBytes;
}

export const IMAGE_SIZES = {
  hero: '100vw',
  card: '(max-width: 575px) 90vw, (max-width: 991px) 45vw, 400px',
  scenic: '(max-width: 991px) 100vw, 358px',
  blogGrid: '(max-width: 767px) 100vw, 33vw',
  icon: '30px',
  avatar: '25px',
  logo: '190px'
};

function pickUrl(info, fields) {
  for (let i = 0; i < fields.length; i += 1) {
    const url = info[fields[i]];
    if (url) return url;
  }
  return info.fileUrl || info.file_url || null;
}

export function isGifFileInfo(fileInfo) {
  const info = asFileInfo(fileInfo);
  if (!info) return false;
  const filename = `${info.filename || info.cleanFilename || ''}`.toLowerCase();
  const url = `${info.fileUrl || info.file_url || ''}`.toLowerCase();
  return filename.endsWith('.gif') || url.includes('.gif');
}

export function isGifSlide(slide) {
  if (!slide || typeof slide !== 'object') return false;
  return isGifFileInfo(slide.fileInfo || slide.file_info);
}

/** Keep CMS order but show optimized raster slides before GIF/test assets in the hero. */
export function sortSlidesForHeroDisplay(slides) {
  if (!Array.isArray(slides) || slides.length <= 1) return slides || [];

  return [...slides].sort((a, b) => {
    const aGif = isGifSlide(a);
    const bGif = isGifSlide(b);
    if (aGif === bGif) return (a.position || 0) - (b.position || 0);
    return aGif ? 1 : -1;
  });
}

/**
 * Pick the best CDN URL for display context.
 * Hero/full: original by default; compressed large/medium only if byte_size > 1MB.
 * @param {object|string|null} fileInfo - API file_info / image hash or raw URL
 * @param {{ preset?: 'icon'|'thumb'|'card'|'hero'|'logo'|'full' }} options
 */
export function getOptimizedImageUrl(fileInfo, options = {}) {
  const { preset = 'card' } = options;
  const info = asFileInfo(fileInfo);
  if (!info) return null;

  if (preset === 'hero' || preset === 'full') {
    const resolvedPreset = isHeavyFileInfo(info) ? 'heroHeavy' : 'full';
    return pickUrl(info, PRESET_FIELDS[resolvedPreset]);
  }

  const fields = PRESET_FIELDS[preset] || PRESET_FIELDS.card;
  return pickUrl(info, fields);
}

/** Build srcset when API exposes responsive variants. */
export function getImageSrcSet(fileInfo, options = {}) {
  const { includeFull = false } = options;
  const info = asFileInfo(fileInfo);
  if (!info) return undefined;

  const candidates = [
    [info.fileUrlIcon || info.file_url_icon, 64],
    [info.fileUrlMinified || info.file_url_minified, 150],
    [info.fileUrlSmall || info.file_url_small, 480],
    [info.fileUrlMedium || info.file_url_medium, 960],
    [info.fileUrlLarge || info.file_url_large, 1600]
  ];

  if (includeFull) {
    candidates.push([info.fileUrl || info.file_url, 2400]);
  }

  const seen = new Set();
  const parts = [];

  candidates.forEach(([url, width]) => {
    if (!url || seen.has(url)) return;
    seen.add(url);
    parts.push(`${url} ${width}w`);
  });

  return parts.length > 1 ? parts.join(', ') : undefined;
}

export function getHeroPreloadUrlFromSlide(slide) {
  if (!slide || typeof slide !== 'object' || isGifSlide(slide)) return null;
  return getOptimizedImageUrl(slide.fileInfo || slide.file_info, {
    preset: 'hero'
  });
}

export function getHeroPreloadUrlFromSlides(slides) {
  if (!Array.isArray(slides)) return null;
  for (let i = 0; i < slides.length; i += 1) {
    const url = getHeroPreloadUrlFromSlide(slides[i]);
    if (url) return url;
  }
  return null;
}

export function getFirstHeroSlide(slides) {
  if (!Array.isArray(slides) || slides.length === 0) return null;
  const sorted = sortSlidesForHeroDisplay(slides);
  return sorted[0] || null;
}
